import { useState, useEffect, useContext } from "react";
import { UserContext } from "../../Context";
import { useNavigate, useLocation } from "react-router-dom";
import { CommonTable, Loader } from "../../Components/Common";
import { MdAdd } from "react-icons/md";
import { FiInfo, FiTrash2, FiFilter } from "react-icons/fi";
import {
  PAYROLL_LABELS,
  PAYROLL_COLUMNS,
  getPayrollStatusColor,
  PAYROLL_STATUS,
  PAYROLL_STATUS_OPTIONS,
  ROLES,
} from "./constants";
import { formatDateDDMMYYYY, toProperCase } from "../../Utils";
import { usePayroll } from "./Hooks/usePayroll.js";
import { RESET_PAGINATION } from "../../Constants/Strings";
import { ADD_SALARY_MASTER, DETAIL_SALARY_MASTER } from "../../routes.js";
import InfiniteScroll from "react-infinite-scroll-component";
import { TOAST_MESSAGES } from "./constants";
import CommonFilter from "../../Components/Common/CommonFilter";

const SalaryMaster = () => {
  const {
    loading,
    filters,
    setFilters,
    setPagination,
    fetchPayroll,
    loadMorePayroll,
    pagination,
    payroll,
    setPayroll,
    removePayroll,
  } = usePayroll();
  const location = useLocation();
  const { user } = useContext(UserContext);
  const [selectedUserOption, setSelectedUserOption] = useState(() => {
    return (
      location.state?.selectedUser || null
    );
  });
  const [tempFilters, setTempFilters] = useState(filters);
  const [showFilter, setShowFilter] = useState(false);

  const loadPayroll = async () => {
    const data = await fetchPayroll(
      { offset: 0, limit: pagination.limit },
      true
    );
    setPayroll(data);
  };

  const renderField = (key, row) => {
    if (key === "status") {
      return (
        <span
          className={`px-1.5 py-1 rounded-md text-xs font-medium ${getPayrollStatusColor(
            row.status
          )}`}
        >
          {toProperCase(row.status)}
        </span>
      );
    }
    if (key === "start_date") return formatDateDDMMYYYY(row.start_date) || "-";
    if (key === "end_date") return formatDateDDMMYYYY(row.end_date) || "-";
    if (key === "action") {
      return (
        <div className="flex items-center gap-4">
          {/* Detail Button */}
          <FiInfo
            size={17}
            onClick={() => handleDetail(row.id)}
            className="h-5 w-5 text-blue-500 cursor-pointer hover:text-blue-700 transition-colors"
          />

          {/* Delete Button (Only HR & Pending) */}
          {[ROLES.HR, ROLES.DIRECTOR_MANAGEMENT].includes(user?.role) && (
            <FiTrash2
              size={17}
              className={`text-red-500 ${row.status !== PAYROLL_STATUS.PENDING_FOR_APPROVAL
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer hover:text-red-700"
                }`}
              onClick={() =>
                row.status === PAYROLL_STATUS.PENDING_FOR_APPROVAL &&
                handleDelete(row.id)
              }
            />
          )}
        </div>
      );
    }
    if (typeof row[key] === "boolean") {
      return row[key] ? "Yes" : "No";
    }
    return row[key] || "-";
  };

  const navigate = useNavigate();

  const handleAddClick = () => {
    navigate(ADD_SALARY_MASTER);
  };

  const handleDetail = (id) => {
    navigate(DETAIL_SALARY_MASTER.replace(":payrollId", id));
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(TOAST_MESSAGES.PAYROLL_DELETE_ALERT);
    if (!confirmed) return;

    const success = await removePayroll(id);
    if (success) loadPayroll();
  };

  const handleApplyFilters = () => {
    if (tempFilters.user === '') {
      return
    }
    setSelectedUserOption(tempFilters.user);
    tempFilters.user = tempFilters.user?.id;
    setFilters(tempFilters);
    setPagination(RESET_PAGINATION);
    setPayroll([]);
    setShowFilter(false);

    // Update navigation state for back button
    navigate(".", { state: { filters: tempFilters, selectedUser: tempFilters.user }, replace: true });
  };

  const handleClearFilters = () => {
    const cleared = { status: "", user: "" };
    setSelectedUserOption(null);
    setTempFilters(cleared);
    setFilters(cleared);
    setPagination(RESET_PAGINATION);
    setPayroll([]);

    navigate(".", { state: { filters: cleared, selectedUser: null }, replace: true });
  };

  const handleCancelFilters = () => {
    setTempFilters(filters);
    setShowFilter(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const renderMobileCards = () => {
    return (
      <div className="md:hidden space-y-2 mt-2">
        {payroll.length === 0 ? (
          <p className="text-center text-gray-600">No documents found.</p>
        ) : (
          payroll.map((row) => {
            return (
              <div
                key={row.id}
                className="border rounded-lg shadow-sm p-2 bg-white"
              >
                <div className="mb-2">
                  <p className="font-semibold text-primary flex justify-between">
                    <span>{row.userDetails.first_name}</span>
                    <span className="text-gray-900 font-normal">
                      {formatCurrency(row.gross_salary)}
                    </span>
                  </p>
                  <p className="justify-between mt-1">
                    <span className="text-gray-900 font-normal">
                      {new Date(row.start_date).toLocaleDateString("en-GB")} -{" "}
                      {new Date(row.end_date).toLocaleDateString("en-GB")}
                    </span>
                  </p>

                  <p className="flex justify-between mt-2">
                    <span
                      className={`px-1.5 py-1 rounded-md text-xs font-medium ${getPayrollStatusColor(
                        row.status
                      )}`}
                    >
                      {toProperCase(row.status)}
                    </span>
                    <div className="flex items-center gap-4">
                      {/* Detail Button */}
                      <FiInfo
                        size={17}
                        onClick={() => handleDetail(row.id)}
                        className="h-5 w-5 text-blue-500 cursor-pointer hover:text-blue-700 transition-colors"
                      />
                      {/* Delete Button (Only HR & Pending) */}
                      {[ROLES.HR, ROLES.DIRECTOR_MANAGEMENT].includes(user?.role) && (
                        <FiTrash2
                          size={17}
                          className={`text-red-500 ${row.status !== PAYROLL_STATUS.PENDING_FOR_APPROVAL
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer hover:text-red-700"
                            }`}
                          onClick={() =>
                            row.status ===
                            PAYROLL_STATUS.PENDING_FOR_APPROVAL &&
                            handleDelete(row.id)
                          }
                        />
                      )}
                    </div>
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  useEffect(() => {
    loadPayroll();
  }, [filters]);

  return (
    <div className="px-4 py-4">
      <div className="flex flex-col w-full">
        {/* Heading + Add button */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold text-primary">Salary Master</h2>
          <div className="flex items-center gap-2">
            <button
              className="px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
              onClick={() => {
                setTempFilters(filters);
                setShowFilter(true);
              }}
            >
              <FiFilter className="text-xl" />
            </button>
            {[ROLES.HR, ROLES.DIRECTOR_MANAGEMENT].includes(user?.role) && (
              <button
                className="px-4 py-1.5 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-md flex items-center gap-2"
                onClick={handleAddClick}
              >
                <MdAdd size={18} /> Add
              </button>
            )}
          </div>
        </div>

        {/* Filter Modal */}
        {showFilter && (
          <CommonFilter
            title="Salary Master Filter"
            tempFilters={tempFilters}
            setTempFilters={setTempFilters}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            onCancel={handleCancelFilters}
            statusOptions={[
              { label: "All", value: "" },
              ...PAYROLL_STATUS_OPTIONS,
            ]}
            useSearchableUser={true}
            selectedSearchableValue={selectedUserOption}
          />
        )}
      </div>

      <InfiniteScroll
        dataLength={payroll.length}
        next={loadMorePayroll}
        hasMore={pagination.offset < pagination.count}
        scrollableTarget="scrollDiv"
      >
        <div
          className="h-[70vh] overflow-x-auto overflow-y-auto px-4 py-2 mt-3  hidden md:block"
          id="scrollDiv"
        >
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <Loader />
            </div>
          ) : (
            <CommonTable
              tableColumns={PAYROLL_COLUMNS}
              columnLabels={PAYROLL_LABELS}
              tableRows={payroll}
              renderField={renderField}
              headerClassName="bg-gray-100"
              rowClick={null}
            />
          )}
        </div>
      </InfiniteScroll>
      {/* Mobile Card View */}
      {renderMobileCards()}
    </div>
  );
};
export default SalaryMaster;
